import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from './AuthContext';
import { fmtCurrency } from '../lib/display-labels';
import { retryUnlessRefused } from '../lib/load-failure';
import { readCachedTenantZone, resolveTenantZone, writeCachedTenantZone, type TenantZoneState } from '../lib/tenant-zone';

export type Country = 'CA' | 'US';
export type UnitSystem = 'metric' | 'imperial';
export type Currency = 'CAD' | 'USD';
export type WellIdFormat = 'LSD' | 'API';
export type DateFormat = 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY';
export type TemperatureUnit = 'celsius' | 'fahrenheit';
export type TaxSystem = 'CA_gst_pst' | 'CA_hst' | 'US_state' | 'US_exempt';

export interface CompanyLocale {
  country: Country;
  currency: Currency;
  unit_system: UnitSystem;
  well_id_format: WellIdFormat;
  tax_system: TaxSystem;
  date_format: DateFormat;
  /**
   * `companies.time_zone`, from the server or from this device's copy of the
   * server's last answer — NEVER a default. Null until one of those exists.
   * Read it through `useTenantZone()` when the difference between "loading",
   * "failed" and "known" matters; see lib/tenant-zone.ts.
   */
  time_zone: string | null;
  temperature_unit: TemperatureUnit;
}

/*
 * The non-zone fields still default while the call is in flight. The zone does
 * NOT, and that is the whole of fe#tenant-zone: this object used to carry
 * `time_zone: 'America/Edmonton'`, so every Chicago tenant's dates were
 * computed in Alberta until /company/locale answered — and forever, on a
 * device that could not reach it.
 */
const DEFAULT_LOCALE: Omit<CompanyLocale, 'time_zone'> = {
  country: 'CA',
  currency: 'CAD',
  unit_system: 'metric',
  well_id_format: 'LSD',
  tax_system: 'CA_gst_pst',
  date_format: 'YYYY-MM-DD',
  temperature_unit: 'celsius',
};

function buildHelpers(raw: CompanyLocale, zone: TenantZoneState, tenantCurrency: Currency | null) {
  const locale = raw;
  const isMetric = locale.unit_system === 'metric';
  const isCanadian = locale.country === 'CA';
  const isAmerican = locale.country === 'US';
  const flag = isCanadian ? '🇨🇦' : '🇺🇸';

  return {
    locale,
    /** The tenant zone's three states. Prefer `useTenantZone()`. */
    zone,
    isMetric,
    isCanadian,
    isAmerican,
    flag,

    // Unit formatting (assumes value is already in the display unit)
    formatDepth: (value: number) => (isMetric ? `${value} m` : `${value} ft`),
    formatPressure: (value: number) => (isMetric ? `${value} kPa` : `${value} PSI`),
    formatTemperature: (value: number) => (isMetric ? `${value}°C` : `${value}°F`),
    formatDistance: (value: number) => (isMetric ? `${value} km` : `${value} mi`),
    formatWeight: (value: number) => (isMetric ? `${value} kg` : `${value} lbs`),

    // Storage <-> display conversions (canonical storage is metric)
    toDisplayDepth: (metres: number) => (isMetric ? metres : metres * 3.28084),
    toDisplayPressure: (kpa: number) => (isMetric ? kpa : kpa * 0.145038),
    toDisplayTemp: (celsius: number) => (isMetric ? celsius : (celsius * 9) / 5 + 32),
    toStorageDepth: (display: number) => (isMetric ? display : display / 3.28084),
    toStoragePressure: (display: number) => (isMetric ? display : display / 0.145038),
    toStorageTemp: (display: number) => (isMetric ? display : ((display - 32) * 5) / 9),

    // Currency — ONE convention, and it is fmtCurrency's. This used to be a
    // third: `$1,234.50 CAD`, with a `formatNum` that returned '0.00' for a
    // non-finite input, so an absent amount read as free. It had no callers, and
    // neither did PriceBooks' identical copy, so the suffix form is gone rather
    // than reconciled.
    //
    // `currency` is PER VALUE, not per tenant: invoices, quotes, tickets, rate
    // sheets and eleven other tables each carry their own `currency` column,
    // seeded from `companies.currency` but authoritative once written. Pass the
    // row's own code where there is one; `currencyCode` below is the tenant
    // default for the rows that have none.
    currencySymbol: '$',
    currencyCode: locale.currency,
    /**
     * `companies.currency` as the SERVER answered it, or null until it has.
     * `currencyCode` above falls back to 'CAD' while /company/locale is in
     * flight, so a US tenant's figures would read "CAD" first and flip a
     * moment later — a plausible wrong label. Use this where a label must not
     * guess; see `useTenantCurrency`.
     */
    tenantCurrency,
    formatCurrency: (amount: number | string | null | undefined, currency?: string | null) =>
      fmtCurrency(amount, currency ?? locale.currency),

    // Well ID
    wellIdFormat: locale.well_id_format,
    wellIdLabel: locale.well_id_format === 'LSD' ? 'LSD / UWI' : 'API Number',
    wellIdPlaceholder:
      locale.well_id_format === 'LSD' ? '100/04-22-082-19W6/00' : '42-317-20130-0000',

    // Tax
    taxLabel1: isCanadian ? 'GST' : 'State Tax',
    taxLabel2: isCanadian ? 'PST/HST' : null,
    showTaxExempt: locale.tax_system === 'US_exempt',

    // Field labels
    depthLabel: isMetric ? 'Depth (m)' : 'Depth (ft)',
    pressureLabel: isMetric ? 'Pressure (kPa)' : 'Pressure (PSI)',
    tempLabel: isMetric ? 'Temperature (°C)' : 'Temperature (°F)',
  };
}

export type LocaleValue = ReturnType<typeof buildHelpers>;

export const LocaleContext = createContext<LocaleValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, company, user } = useAuth();
  const companyId: string | null = company?.id ?? user?.company_id ?? user?.companyId ?? null;

  const query = useQuery({
    // The company id is in the key so a second tenant on this device never
    // reads the first one's answer, cached or in flight.
    queryKey: ['company', 'locale', companyId],
    queryFn: () =>
      api.company.getLocale().then((r) => {
        const body = r.data;
        return (body && typeof body === 'object' && 'data' in body ? body.data : body) as Partial<CompanyLocale> | null;
      }),
    enabled: isAuthenticated && !isLoading,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused(),
  });

  const { data } = query;
  const cached = useMemo(() => readCachedTenantZone(companyId), [companyId]);

  const signedIn = isAuthenticated && !isLoading;
  const serverZone = query.status === 'success' ? (data?.time_zone ?? null) : undefined;
  const zone = useMemo(
    () =>
      resolveTenantZone({
        signedIn,
        status: query.status,
        fetchStatus: query.fetchStatus,
        serverZone,
        error: query.error,
        cached,
      }),
    [signedIn, query.status, query.fetchStatus, serverZone, query.error, cached],
  );

  // Every successful answer refreshes this device's copy — and only a
  // successful answer: a failed or paused call leaves the last good one alone.
  const liveZone = zone.status === 'ready' && zone.source === 'server' ? zone.zone : null;
  useEffect(() => {
    if (liveZone) writeCachedTenantZone(companyId, liveZone);
  }, [companyId, liveZone]);

  const value = useMemo(() => {
    const merged: CompanyLocale = {
      ...DEFAULT_LOCALE,
      ...(data ?? {}),
      time_zone: zone.status === 'ready' ? zone.zone : null,
    } as CompanyLocale;
    const known: Currency | null = data?.currency === 'CAD' || data?.currency === 'USD' ? data.currency : null;
    return buildHelpers(merged, zone, known);
  }, [data, zone]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

/**
 * The tenant's currency code, or null when it is not known — still loading, a
 * failed read, a value outside CAD/USD, or no LocaleProvider above (a screen
 * mounted on its own in a test). A null label is omitted, never guessed.
 */
export function useTenantCurrency(): Currency | null {
  return useContext(LocaleContext)?.tenantCurrency ?? null;
}
